import { useState } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import {
  getMFAStatus, setupTOTP, verifyTOTP, setupEmailMFA, verifyEmailMFA,
  disableMFAMethod, setMFAPreferredMethod, MFAStatus,
} from '../api/client'
import { useAuth } from '../auth/AuthContext'
import TOTPQRCode from '../components/TOTPQRCode'

const cardStyle: React.CSSProperties = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 24, marginBottom: 16, maxWidth: 520 }
const btnStyle: React.CSSProperties = { padding: '7px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 }
const inputSt: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '8px 12px',
  border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginBottom: 12,
}
const rowStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '12px 0', borderBottom: '1px solid #f3f4f6',
}

type MethodKey = 'totp' | 'email'
type SetupStep = null | { method: 'totp'; secret: string; otpauthUrl: string } | { method: 'email' }

export default function Account() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const { data: mfaStatus } = useQuery<MFAStatus>({ queryKey: ['mfa-status'], queryFn: getMFAStatus })

  const [setupStep, setSetupStep] = useState<SetupStep>(null)
  const [code, setCode] = useState('')
  const [verifyError, setVerifyError] = useState<string | null>(null)
  const [setupWarning, setSetupWarning] = useState<string | null>(null)

  const [disabling, setDisabling] = useState<MethodKey | null>(null)
  const [disablePassword, setDisablePassword] = useState('')
  const [disableError, setDisableError] = useState<string | null>(null)

  const invalidate = () => qc.invalidateQueries({ queryKey: ['mfa-status'] })

  const startTOTPMut = useMutation({
    mutationFn: setupTOTP,
    onSuccess: (res) => { setSetupStep({ method: 'totp', secret: res.secret, otpauthUrl: res.otpauthUrl }); setCode(''); setVerifyError(null) },
  })
  const startEmailMut = useMutation({
    mutationFn: setupEmailMFA,
    onSuccess: (res) => {
      setSetupStep({ method: 'email' })
      setCode('')
      setVerifyError(null)
      setSetupWarning(res.sent ? null : (res.warning ?? 'Code failed to send.'))
    },
  })
  const verifyMut = useMutation({
    mutationFn: () => (setupStep?.method === 'totp' ? verifyTOTP(code) : verifyEmailMFA(code)),
    onSuccess: () => {
      setSetupStep(null)
      invalidate()
    },
    onError: () => setVerifyError('Invalid or expired code.'),
  })
  const disableMut = useMutation({
    mutationFn: () => disableMFAMethod(disabling as MethodKey, disablePassword),
    onSuccess: () => {
      setDisabling(null)
      setDisablePassword('')
      setDisableError(null)
      invalidate()
    },
    onError: () => setDisableError('Incorrect password.'),
  })
  const preferredMut = useMutation({
    mutationFn: (method: MethodKey) => setMFAPreferredMethod(method),
    onSuccess: invalidate,
  })

  const cancelSetup = () => { setSetupStep(null); setCode(''); setVerifyError(null); setSetupWarning(null) }

  const totpEnabled = !!mfaStatus?.totpEnabled
  const emailEnabled = !!mfaStatus?.emailEnabled
  const bothEnabled = totpEnabled && emailEnabled

  const methodRow = (method: MethodKey, label: string, enabled: boolean, onSetup: () => void, pending: boolean) => (
    <div style={rowStyle}>
      <div>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 500 }}>{label}</p>
        <p style={{ margin: '2px 0 0', fontSize: 12.5, color: '#6b7280' }}>
          {enabled
            ? (mfaStatus?.method === method ? 'Enabled · default sign-in method' : 'Enabled · fallback method')
            : 'Not set up'}
        </p>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {enabled && bothEnabled && mfaStatus?.method !== method && (
          <button
            style={{ ...btnStyle, background: '#f3f4f6', color: '#374151' }}
            onClick={() => preferredMut.mutate(method)}
            disabled={preferredMut.isPending}
          >
            Make default
          </button>
        )}
        {enabled ? (
          disabling === method ? null : (
            <button style={{ ...btnStyle, background: '#fee2e2', color: '#991b1b' }} onClick={() => { setDisabling(method); setDisablePassword(''); setDisableError(null) }}>
              Disable
            </button>
          )
        ) : (
          <button style={{ ...btnStyle, background: '#2563eb', color: '#fff' }} onClick={onSetup} disabled={pending}>
            {pending ? 'Starting…' : 'Set up'}
          </button>
        )}
      </div>
    </div>
  )

  return (
    <div>
      <h1 style={{ margin: '0 0 24px', fontSize: 24, fontWeight: 700 }}>Account</h1>

      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>Profile</h3>
        <p style={{ fontSize: 14, color: '#374151', margin: '8px 0 0' }}>{user?.Email}</p>
      </div>

      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>Two-Factor Authentication</h3>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 16px' }}>
          Required for every account. Set up both an authenticator app and email codes so you
          always have a fallback if you lose access to one of them.
        </p>

        {!setupStep && (
          <>
            {methodRow('totp', 'Authenticator App', totpEnabled, () => startTOTPMut.mutate(), startTOTPMut.isPending)}
            {methodRow('email', 'Email Code', emailEnabled, () => startEmailMut.mutate(), startEmailMut.isPending)}

            {disabling && (
              <div style={{ marginTop: 16, padding: 16, background: '#fef2f2', borderRadius: 6 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                  Confirm your password to disable {disabling === 'totp' ? 'the authenticator app' : 'email codes'}
                </label>
                <input
                  style={inputSt}
                  type="password"
                  value={disablePassword}
                  onChange={e => setDisablePassword(e.target.value)}
                  autoFocus
                />
                {disableError && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{disableError}</p>}
                {((disabling === 'totp' && !emailEnabled) || (disabling === 'email' && !totpEnabled)) && (
                  <p style={{ color: '#92400e', background: '#fef3c7', borderRadius: 6, padding: '8px 12px', fontSize: 13, margin: '0 0 12px' }}>
                    This is your only MFA method — disabling it means you'll be asked to set up MFA again next time you sign in.
                  </p>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    style={{ ...btnStyle, background: '#dc2626', color: '#fff' }}
                    onClick={() => disableMut.mutate()}
                    disabled={disableMut.isPending || !disablePassword}
                  >
                    {disableMut.isPending ? 'Disabling…' : 'Confirm Disable'}
                  </button>
                  <button
                    style={{ ...btnStyle, background: '#f3f4f6', color: '#374151' }}
                    onClick={() => { setDisabling(null); setDisablePassword(''); setDisableError(null) }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {setupStep?.method === 'totp' && (
          <div>
            <p style={{ fontSize: 13, color: '#374151', margin: '0 0 8px' }}>
              Scan this QR code with your authenticator app (Google Authenticator, Authy,
              1Password, etc.), or enter the secret manually, then enter the 6-digit code it shows.
            </p>
            <TOTPQRCode otpauthUrl={setupStep.otpauthUrl} />
            <div style={{
              background: '#f3f4f6', borderRadius: 6, padding: '10px 14px',
              fontFamily: 'monospace', fontSize: 14, wordBreak: 'break-all', marginBottom: 16,
            }}>
              {setupStep.secret}
            </div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Code from app</label>
            <input
              style={{ ...inputSt, textAlign: 'center', fontSize: 18, letterSpacing: 4, fontFamily: 'monospace' }}
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              autoFocus
            />
            {verifyError && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{verifyError}</p>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                style={{ ...btnStyle, background: '#2563eb', color: '#fff' }}
                onClick={() => verifyMut.mutate()}
                disabled={verifyMut.isPending || code.length !== 6}
              >
                {verifyMut.isPending ? 'Verifying…' : 'Verify & Enable'}
              </button>
              <button style={{ ...btnStyle, background: '#f3f4f6', color: '#374151' }} onClick={cancelSetup}>Cancel</button>
            </div>
          </div>
        )}

        {setupStep?.method === 'email' && (
          <div>
            <p style={{ fontSize: 13, color: '#374151', margin: '0 0 12px' }}>
              Enter the 6-digit code we just emailed you.
            </p>
            {setupWarning && (
              <p style={{ color: '#92400e', background: '#fef3c7', borderRadius: 6, padding: '8px 12px', fontSize: 13, margin: '0 0 12px' }}>
                {setupWarning}
              </p>
            )}
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Code</label>
            <input
              style={{ ...inputSt, textAlign: 'center', fontSize: 18, letterSpacing: 4, fontFamily: 'monospace' }}
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              autoFocus
            />
            {verifyError && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{verifyError}</p>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                style={{ ...btnStyle, background: '#2563eb', color: '#fff' }}
                onClick={() => verifyMut.mutate()}
                disabled={verifyMut.isPending || code.length !== 6}
              >
                {verifyMut.isPending ? 'Verifying…' : 'Verify & Enable'}
              </button>
              <button style={{ ...btnStyle, background: '#f3f4f6', color: '#374151' }} onClick={cancelSetup}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
